/**
 * Gneiss Design System - Design Tokens
 * Extracted from Figma using MCP
 * https://www.figma.com/design/OeZyCwMOQGhiBSdNslhBMo/Gneiss-Design-System
 */

// ============================================================================
// Text Colors
// ============================================================================
export const textColors = {
  primary: '#021e38',      // Text/primary
  secondary: '#405f80',    // Text/secondary
  inverse: '#ffffff',      // Text/inverse
} as const;

// ============================================================================
// Icon Colors
// ============================================================================
export const iconColors = {
  primary: '#021e38',              // Icon/primary
  error: '#cd0e04',                // Icon/error
  warning: '#dc7f3b',              // Icon/warning
  success: '#26ab76',              // Icon/success
  alertInfoSecondary: '#405f80',   // Icon/alert-info-secondary
  inverse: '#ffffff',              // Icon/inverse
} as const;

// ============================================================================
// Alert Background Colors
// ============================================================================
export const alertBackgrounds = {
  white: '#ffffff',                    // Alert/bg-white
  error: {
    primary: '#cd0e04',                // Alert/bg-error-primary
    secondary: '#ffc7bb',              // Alert/bg-error-secondary
  },
  warning: {
    primary: '#dc7f3b',                // Alert/bg-warning-primary
    secondary: '#f9ddb5',              // Alert/bg-warning-secondary
  },
  info: {
    primary: '#021e38',                // Alert/bg-neutralDark-primary
    secondary: '#b9c4d1',              // Alert/bg-neutralDark-secondary
  },
  success: {
    primary: '#26ab76',                // Alert/bg-success-primary
    secondary: '#d2efe7',              // Alert/bg-success-secondary
  },
} as const;

// ============================================================================
// Border Colors
// ============================================================================
export const borderColors = {
  error: '#cd0e04',        // Border/error
  warning: '#dc7f3b',      // Border/warning
  success: '#26ab76',      // Border/success
  secondary: '#16476e',    // Border/secondary (used for info)
} as const;

// ============================================================================
// Border Tokens
// ============================================================================
export const borderTokens = {
  radius: '12px',          // Colors/Border/Border-Radius: 12
} as const;

// ============================================================================
// Alert Colors (Backward Compatible)
// ============================================================================
export const alertColors = {
  error: {
    primary: alertBackgrounds.error.primary,
    secondary: alertBackgrounds.error.secondary,
    border: borderColors.error,
  },
  warning: {
    primary: alertBackgrounds.warning.primary,
    secondary: alertBackgrounds.warning.secondary,
    border: borderColors.warning,
  },
  info: {
    primary: alertBackgrounds.info.primary,
    secondary: alertBackgrounds.info.secondary,
    border: borderColors.secondary,
  },
  success: {
    primary: alertBackgrounds.success.primary,
    secondary: alertBackgrounds.success.secondary,
    border: borderColors.success,
  },
} as const;

// ============================================================================
// Background Colors (Backward Compatible)
// ============================================================================
export const backgroundColors = {
  white: alertBackgrounds.white,
} as const;

// ============================================================================
// Typography Tokens
// ============================================================================
export const fontFamily = {
  title: 'Inclusive Sans',   // font/family/title
  body: 'Inclusive Sans',    // font/family/body
} as const;

export const fontSize = {
  xxl: '48px',    // font/size/xxl
  md: '16px',     // font/size/md
  sm: '14px',     // font/size/sm
} as const;

export const fontWeight = {
  regular: 400,   // font/weight/Regular (400)
  medium: 500,    // font/weight/SemiBold (500)
  semibold: 600,  // font/weight/Bold (600)
} as const;

export const lineHeight = {
  spacious: '48px',  // font/lineHeignt/Spacious
  standard: '24px',  // font/lineHeignt/Standard
  compact: '20px',   // font/lineHeignt/Compact
} as const;

export const letterSpacing = {
  slightlyLoose: '0.1px',   // font/letterSpacing/slightly-loose
  slightlyTight: '-0.1px',  // font/letterSpacing/slightly-tight
} as const;

// ============================================================================
// Typography Styles (Composite Tokens)
// ============================================================================
export const typography = {
  displayMedium: {
    fontFamily: fontFamily.title,
    fontSize: fontSize.xxl,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.spacious,
    letterSpacing: letterSpacing.slightlyLoose,
  },
  subheadLarge: {
    fontFamily: fontFamily.title,
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeight.standard,
    letterSpacing: letterSpacing.slightlyLoose,
  },
  titleMedium: {
    fontFamily: fontFamily.title,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.standard,
    letterSpacing: letterSpacing.slightlyLoose,
  },
  bodyMedium: {
    fontFamily: fontFamily.body,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.compact,
    letterSpacing: letterSpacing.slightlyTight,
  },
  labelLarge: {
    fontFamily: fontFamily.body,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.compact,
    letterSpacing: letterSpacing.slightlyTight,
  },
} as const;

// ============================================================================
// Type Exports
// ============================================================================
export type AlertColorKey = keyof typeof alertColors;
export type AlertColorVariant = keyof typeof alertColors.error;
export type TextColorKey = keyof typeof textColors;
export type IconColorKey = keyof typeof iconColors;
export type BorderColorKey = keyof typeof borderColors;